import React, { useState, useRef } from "react";
import {
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import axios from "axios";

// Import images
import MessageIcon from "../Images/Message.png";
import SettingsIcon from "../Images/Settings.png";
import RocketIcon from "../Images/Rocket.png";
import GraphIcon from "../Images/Graph.png";

const DOMAIN = process.env.REACT_APP_DOMAIN;

const ReadyToPartner = () => {
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toasterOpen, setToasterOpen] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterSeverity, setToasterSeverity] = useState("success");
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: "",
    organization: "",
    designation: "",
    contactNumber: "",
    email: "",
    category: "",
    details: "",
    linkedinId: "",
    proposalFile: null,
  });

  const handleContactClick = () => {
    setContactDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setContactDialogOpen(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const clearForm = () => {
    setFormData({
      fullName: "",
      organization: "",
      designation: "",
      contactNumber: "",
      email: "",
      category: "",
      details: "",
      linkedinId: "",
      proposalFile: null,
    });
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const showToaster = (message, severity = "success") => {
    setToasterMessage(message);
    setToasterSeverity(severity);
    setToasterOpen(true);
  };

  const handleCloseToaster = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToasterOpen(false);
  };

  const handleUploadProposal = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, proposalFile: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append("fullName", formData.fullName);
    data.append("organization", formData.organization);
    data.append("designation", formData.designation);
    data.append("contactNo", formData.contactNumber);
    data.append("mailId", formData.email);
    data.append("MarkoKnowPartner", formData.category);
    data.append("details", formData.details);
    data.append("linkedinId", formData.linkedinId);
    if (formData.proposalFile) {
      data.append("file", formData.proposalFile);
    }

    setLoading(true);
    try {
      await axios.post(`${DOMAIN}/api/postPartner`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          authorization: window.localStorage.getItem("token"),
        },
      });
      showToaster(
        "Thank you for your interest! We will reach out to you soon.",
        "success"
      );
      clearForm();
      setContactDialogOpen(false);
    } catch (err) {
      console.error(err);
      showToaster("Internal Server Error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const workSteps = [
    {
      icon: MessageIcon,
      title: "Initial Conversation",
      subtitle: "Connect & Discuss Opportunities",
      showText: true, // Show text below image
    },
    {
      icon: SettingsIcon,
      title: "Co-Design Partnerships",
      subtitle: "Collaborate on Solutions",
      showText: true, // Show text below image
    },
    {
      icon: RocketIcon,
      title: "Finalization & Sign Offs",
      subtitle: "Complete Legal Formalities",
      showText: true, // Show text below image
    },
    {
      icon: GraphIcon,
      title: "Measure & Grow",
      subtitle: "Track Success & Scale",
      showText: true, // Show text below image
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Main Hero Section */}
      <Container
        sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3, md: 4 } }}
      >
        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            background: "#6a68c6",
            borderRadius: { xs: 3, md: 4 },
            p: { xs: 3, sm: 4, md: 6 },
            color: "white",
            textAlign: { xs: "center", md: "left" },
            // width: "100%",
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: "bold",
              mb: { xs: 2, md: 3 },
              fontSize: {
                xs: "1.8rem",
                sm: "2.2rem",
                md: "3rem",
                lg: "3.5rem",
              },
              lineHeight: 1.2,
            }}
          >
            Ready To Partner?
          </Typography>

          <Typography
            variant="h4"
            sx={{
              mb: { xs: 3, md: 4 },
              fontWeight: 500,
              fontSize: {
                xs: "1rem",
                sm: "1.3rem",
                md: "1.8rem",
                lg: "2.2rem",
              },
              wordBreak: "break-all",
            }}
          >
            partnerships@markoknow.com
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleContactClick}
            sx={{
              backgroundColor: "#1e293b",
              color: "white",
              px: { xs: 3, sm: 4, md: 6 },
              py: { xs: 1.2, sm: 1.5, md: 2 },
              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
              fontWeight: 600,
              borderRadius: { xs: 1.5, md: 2 },
              "&:hover": {
                backgroundColor: "#334155",
              },
            }}
          >
            Contact Us
          </Button>
        </Paper>
      </Container>

      {/* How It Works Section */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          backgroundColor: "#f8fafc",
          px: { xs: 2, sm: 0 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              mb: { xs: 4, md: 6 },
              color: "#1e293b",
              fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            How It Works
          </Typography>

          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {workSteps.map((step, index) => (
              <Grid item xs={6} sm={3} md={3} key={index}>
                <Box sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      width: { xs: 80, sm: 100, md: 120 },
                      height: { xs: 80, sm: 100, md: 120 },
                      mx: "auto",
                      mb: { xs: 2, md: 3 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      padding: "8px",
                    }}
                  >
                    <img
                      src={step.icon}
                      alt={`${step.title} ${step.subtitle}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Form Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{
          sx: {
            mx: { xs: 1, sm: 2 },
            width: { xs: "calc(100vw - 16px)", sm: "auto" },
            maxHeight: { xs: "95vh", sm: "85vh" },
            borderRadius: { xs: 2, sm: 4 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.3rem", md: "1.5rem" },
            textAlign: "center",
            color: "#1e293b",
          }}
        >
          Partner with Markoknow
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="Full Name"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="Organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="Designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="Contact Number"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <Select
                      value={formData.category}
                      name="category"
                      onChange={handleInputChange}
                      displayEmpty
                      size="medium"
                    >
                      <MenuItem value="">
                        <em>Select Category</em>
                      </MenuItem>
                      <MenuItem value="startup">Startup</MenuItem>
                      <MenuItem value="investor">Investor</MenuItem>
                      <MenuItem value="customer">Customer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextField
                fullWidth
                placeholder="Details"
                name="details"
                multiline
                rows={4}
                value={formData.details}
                onChange={handleInputChange}
                margin="normal"
                variant="outlined"
                size="medium"
              />

              <TextField
                fullWidth
                placeholder="LinkedIn ID"
                name="linkedinId"
                value={formData.linkedinId}
                onChange={handleInputChange}
                margin="normal"
                variant="outlined"
                size="medium"
              />

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleUploadProposal}
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{
                    borderColor: "#7c3aed",
                    color: "#7c3aed",
                    "&:hover": {
                      borderColor: "#6d28d9",
                      backgroundColor: "rgba(124, 58, 237, 0.04)",
                    },
                    py: 1.5,
                  }}
                >
                  {formData.proposalFile
                    ? `Uploaded: ${
                        formData.proposalFile.name.length > 25
                          ? formData.proposalFile.name.substring(0, 25) + "..."
                          : formData.proposalFile.name
                      }`
                    : "UPLOAD PROPOSAL (Optional)"}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, sm: 1 },
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            variant="outlined"
            fullWidth={window.innerWidth < 600}
            sx={{
              minWidth: { sm: "120px" },
              py: { xs: 1.2, sm: 1 },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            fullWidth={window.innerWidth < 600}
            disabled={loading}
            sx={{
              backgroundColor: "#7c3aed",
              "&:hover": { backgroundColor: "#6d28d9" },
              minWidth: { sm: "120px" },
              py: { xs: 1.2, sm: 1 },
            }}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toaster/Snackbar */}
      <Snackbar
        open={toasterOpen}
        autoHideDuration={6000}
        onClose={handleCloseToaster}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToaster}
          severity={toasterSeverity}
          sx={{
            width: "100%",
            maxWidth: { xs: "90vw", sm: "400px" },
          }}
        >
          {toasterMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReadyToPartner;
